import React, { useState, useEffect } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Label } from '../components/ui/label';
import { 
  Link2, Copy, CheckCircle, XCircle, AlertTriangle, RefreshCw,
  ExternalLink, FileText, Search, Zap, Globe2, Tag, Settings,
  ArrowRight, Info, Database, Clipboard
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function UTMBuilder() {
  const [activeTab, setActiveTab] = useState('builder');
  const [loading, setLoading] = useState(false);
  
  // Reference data
  const [referenceData, setReferenceData] = useState(null);
  
  // Builder state
  const [formData, setFormData] = useState({
    base_url: '',
    utm_source: '',
    utm_medium: '',
    utm_campaign: '',
    utm_term: '',
    utm_content: '',
    utm_id: ''
  });
  
  // Generated URL
  const [generatedUrl, setGeneratedUrl] = useState(null);
  
  // Validation state
  const [validationResult, setValidationResult] = useState(null);
  
  // Parse state
  const [parseInput, setParseInput] = useState('');
  const [parseResult, setParseResult] = useState(null);
  
  // Records state
  const [records, setRecords] = useState([]);
  const [recordsLoading, setRecordsLoading] = useState(false);

  useEffect(() => {
    fetchReferenceData();
    fetchRecords();
  }, []);

  const fetchReferenceData = async () => {
    try {
      const response = await fetch(`${API_URL}/api/gios/utm/reference`);
      if (response.ok) {
        const data = await response.json();
        setReferenceData(data);
      }
    } catch (error) {
      console.error('Failed to fetch reference data:', error);
    }
  };

  const fetchRecords = async () => {
    setRecordsLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/gios/utm/records`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setRecords(Array.isArray(data) ? data : []);
      }
    } catch (error) {
      console.error('Failed to fetch records:', error);
    } finally {
      setRecordsLoading(false);
    }
  };

  const handleBuildUrl = async () => {
    if (!formData.base_url || !formData.utm_source || !formData.utm_medium || !formData.utm_campaign) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/gios/utm/build`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      
      if (response.ok) {
        const data = await response.json();
        setGeneratedUrl(data);
        toast.success('UTM URL generated successfully');
        
        // Auto-validate
        handleValidate(data.utm_params);
      } else {
        toast.error('Failed to build URL');
      }
    } catch (error) {
      toast.error('Failed to build URL');
    } finally {
      setLoading(false);
    }
  };

  const handleValidate = async (params = null) => {
    const paramsToValidate = params || {
      utm_source: formData.utm_source,
      utm_medium: formData.utm_medium,
      utm_campaign: formData.utm_campaign,
      utm_term: formData.utm_term,
      utm_content: formData.utm_content,
      utm_id: formData.utm_id
    };
    
    try {
      const response = await fetch(`${API_URL}/api/gios/utm/validate`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(paramsToValidate)
      });
      
      if (response.ok) {
        const data = await response.json();
        setValidationResult(data);
      }
    } catch (error) {
      console.error('Validation failed:', error);
    }
  };

  const handleCreateRecord = async () => {
    if (!generatedUrl) {
      toast.error('Please generate a URL first');
      return;
    }
    
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/gios/utm/records`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          client_id: 'default',
          landing_page_url: formData.base_url,
          utm_source: formData.utm_source,
          utm_medium: formData.utm_medium,
          utm_campaign: formData.utm_campaign,
          utm_term: formData.utm_term || null,
          utm_content: formData.utm_content || null,
          utm_id: formData.utm_id || null
        })
      });
      
      if (response.ok) {
        toast.success('UTM record saved successfully');
        fetchRecords();
        
        // Reset form
        setFormData({
          base_url: '',
          utm_source: '',
          utm_medium: '',
          utm_campaign: '',
          utm_term: '',
          utm_content: '',
          utm_id: ''
        });
        setGeneratedUrl(null);
        setValidationResult(null);
      } else {
        toast.error('Failed to save record');
      }
    } catch (error) {
      toast.error('Failed to save record');
    } finally {
      setLoading(false);
    }
  };

  const handleParse = async () => {
    if (!parseInput.trim()) {
      toast.error('Please enter a URL to parse');
      return;
    }
    
    try {
      const response = await fetch(`${API_URL}/api/gios/utm/parse?url=${encodeURIComponent(parseInput)}`, {
        method: 'POST',
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setParseResult(data);
        toast.success('URL parsed successfully');
      } else {
        toast.error('Failed to parse URL');
      }
    } catch (error) {
      toast.error('Failed to parse URL');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const getIntegrityScoreColor = (score) => {
    if (score >= 80) return 'text-emerald-400';
    if (score >= 60) return 'text-amber-400';
    return 'text-red-400';
  };

  const getIntegrityScoreBg = (score) => {
    if (score >= 80) return 'bg-emerald-500/20';
    if (score >= 60) return 'bg-amber-500/20';
    return 'bg-red-500/20';
  };

  const getMediumCategory = (medium) => {
    if (!referenceData?.medium_categories) return 'unknown';
    for (const [category, mediums] of Object.entries(referenceData.medium_categories)) {
      if (mediums.includes(medium?.toLowerCase())) return category;
    }
    return 'other';
  };

  const getMediumCategoryColor = (category) => {
    const colors = {
      paid: 'bg-blue-500/20 text-blue-400',
      organic: 'bg-emerald-500/20 text-emerald-400',
      email: 'bg-purple-500/20 text-purple-400',
      referral: 'bg-amber-500/20 text-amber-400',
      other: 'bg-zinc-500/20 text-zinc-400'
    };
    return colors[category] || colors.other;
  };

  return (
    <DashboardLayout>
      <div className="space-y-6 p-6" data-testid="utm-builder-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-primary/10">
                <Link2 className="w-6 h-6 text-primary" />
              </div>
              <div>
                <h1 className="text-2xl font-semibold tracking-tight">UTM Builder</h1>
                <p className="text-sm text-muted-foreground">
                  Campaign URL Parameter Management
                </p>
              </div>
            </div>
          </div>
          <Badge variant="outline" className="text-xs px-3 py-1 bg-emerald-500/10 text-emerald-400 border-emerald-500/20">
            GIOS v2.0
          </Badge>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-zinc-800/50 border border-zinc-700">
            <TabsTrigger value="builder" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
              <Zap className="w-4 h-4 mr-2" />
              Builder
            </TabsTrigger>
            <TabsTrigger value="records" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
              <Database className="w-4 h-4 mr-2" />
              Records
            </TabsTrigger>
            <TabsTrigger value="parse" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
              <Search className="w-4 h-4 mr-2" />
              Parse
            </TabsTrigger>
            <TabsTrigger value="reference" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
              <Info className="w-4 h-4 mr-2" />
              Reference
            </TabsTrigger>
          </TabsList>

          {/* Builder Tab */}
          <TabsContent value="builder" className="space-y-6 mt-6">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Form Section */}
              <div className="lg:col-span-2 space-y-6">
                <Card className="bg-zinc-900/50 border-zinc-800">
                  <CardHeader className="pb-4">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Settings className="w-5 h-5 text-primary" />
                      UTM Parameters
                    </CardTitle>
                    <CardDescription>
                      Build campaign tracking URLs with standardized UTM parameters
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    {/* Base URL */}
                    <div className="space-y-2">
                      <Label className="text-sm text-zinc-400 flex items-center gap-2">
                        <Globe2 className="w-3.5 h-3.5" />
                        Landing Page URL *
                      </Label>
                      <Input
                        value={formData.base_url}
                        onChange={(e) => setFormData({ ...formData, base_url: e.target.value })}
                        placeholder="https://mediakshetra.com/gios"
                        className="bg-zinc-800/50 border-zinc-700"
                        data-testid="utm-base-url"
                      />
                    </div>

                    {/* Required Parameters */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <Label className="text-sm text-zinc-400 flex items-center gap-2">
                          <Tag className="w-3.5 h-3.5" />
                          utm_source *
                        </Label>
                        <select
                          value={formData.utm_source}
                          onChange={(e) => setFormData({ ...formData, utm_source: e.target.value })}
                          className="w-full h-10 px-3 rounded-md bg-zinc-800/50 border border-zinc-700 text-sm text-zinc-100 focus:outline-none focus:ring-2 focus:ring-primary/50"
                          data-testid="utm-source"
                        >
                          <option value="">Select source</option>
                          <option value="google">google</option>
                          <option value="facebook">facebook</option>
                          <option value="instagram">instagram</option>
                          <option value="linkedin">linkedin</option>
                          <option value="tiktok">tiktok</option>
                          <option value="twitter">twitter</option>
                          <option value="newsletter">newsletter</option>
                          <option value="direct">direct</option>
                        </select>
                      </div>

                      <div className="space-y-2">
                        <Label className="text-sm text-zinc-400 flex items-center gap-2">
                          <Tag className="w-3.5 h-3.5" />
                          utm_medium *
                        </Label>
                        <select
                          value={formData.utm_medium}
                          onChange={(e) => setFormData({ ...formData, utm_medium: e.target.value })}
                          className="w-full h-10 px-3 rounded-md bg-zinc-800/50 border border-zinc-700 text-sm text-zinc-100 focus:outline-none focus:ring-2 focus:ring-primary/50"
                          data-testid="utm-medium"
                        >
                          <option value="">Select medium</option>
                          <optgroup label="Paid">
                            <option value="cpc">cpc</option>
                            <option value="ppc">ppc</option>
                            <option value="paid_social">paid_social</option>
                            <option value="display">display</option>
                            <option value="native">native</option>
                            <option value="retargeting">retargeting</option>
                          </optgroup>
                          <optgroup label="Organic">
                            <option value="organic">organic</option>
                            <option value="organic_social">organic_social</option>
                            <option value="seo">seo</option>
                          </optgroup>
                          <optgroup label="Email">
                            <option value="email">email</option>
                            <option value="newsletter">newsletter</option>
                          </optgroup>
                          <optgroup label="Other">
                            <option value="referral">referral</option>
                            <option value="affiliate">affiliate</option>
                            <option value="direct">direct</option>
                          </optgroup>
                        </select>
                      </div>

                      <div className="space-y-2">
                        <Label className="text-sm text-zinc-400 flex items-center gap-2">
                          <Tag className="w-3.5 h-3.5" />
                          utm_campaign *
                        </Label>
                        <Input
                          value={formData.utm_campaign}
                          onChange={(e) => setFormData({ ...formData, utm_campaign: e.target.value.toLowerCase().replace(/\s+/g, '_') })}
                          placeholder="campaign_name"
                          className="bg-zinc-800/50 border-zinc-700"
                          data-testid="utm-campaign"
                        />
                      </div>
                    </div>

                    {/* Optional Parameters */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <Label className="text-sm text-zinc-400 flex items-center gap-2">
                          <Tag className="w-3.5 h-3.5 opacity-50" />
                          utm_term
                          <span className="text-[10px] text-zinc-600">(optional)</span>
                        </Label>
                        <Input
                          value={formData.utm_term}
                          onChange={(e) => setFormData({ ...formData, utm_term: e.target.value })}
                          placeholder="paid_keywords"
                          className="bg-zinc-800/50 border-zinc-700"
                          data-testid="utm-term"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label className="text-sm text-zinc-400 flex items-center gap-2">
                          <Tag className="w-3.5 h-3.5 opacity-50" />
                          utm_content
                          <span className="text-[10px] text-zinc-600">(optional)</span>
                        </Label>
                        <Input
                          value={formData.utm_content}
                          onChange={(e) => setFormData({ ...formData, utm_content: e.target.value })}
                          placeholder="banner_a"
                          className="bg-zinc-800/50 border-zinc-700"
                          data-testid="utm-content"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label className="text-sm text-zinc-400 flex items-center gap-2">
                          <Tag className="w-3.5 h-3.5 opacity-50" />
                          utm_id
                          <span className="text-[10px] text-zinc-600">(optional)</span>
                        </Label>
                        <Input
                          value={formData.utm_id}
                          onChange={(e) => setFormData({ ...formData, utm_id: e.target.value })}
                          placeholder="camp_123"
                          className="bg-zinc-800/50 border-zinc-700"
                          data-testid="utm-id"
                        />
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex gap-3">
                      <Button
                        onClick={handleBuildUrl}
                        disabled={loading || !formData.base_url || !formData.utm_source || !formData.utm_medium || !formData.utm_campaign}
                        className="flex-1"
                        data-testid="build-url-btn"
                      >
                        {loading ? (
                          <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <Zap className="w-4 h-4 mr-2" />
                        )}
                        Build URL
                      </Button>
                      <Button
                        onClick={handleCreateRecord}
                        disabled={loading || !generatedUrl}
                        variant="outline"
                        className="flex-1"
                        data-testid="save-record-btn"
                      >
                        <Database className="w-4 h-4 mr-2" />
                        Save Record
                      </Button>
                    </div>
                  </CardContent>
                </Card>

                {/* Generated URL */}
                {generatedUrl && (
                  <Card className="bg-zinc-900/50 border-zinc-800 border-primary/20">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm flex items-center gap-2 text-primary">
                        <CheckCircle className="w-4 h-4" />
                        Generated URL
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="p-4 rounded-lg bg-zinc-800/30 border border-zinc-700">
                        <div className="flex items-start justify-between gap-4">
                          <code className="text-sm font-mono text-emerald-300 break-all flex-1">
                            {generatedUrl.utm_url}
                          </code>
                          <div className="flex gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => copyToClipboard(generatedUrl.utm_url)}
                            >
                              <Copy className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => window.open(generatedUrl.utm_url, '_blank')}
                            >
                              <ExternalLink className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                        <div className="flex items-center gap-4 mt-3 text-xs text-zinc-500">
                          <span>{generatedUrl.param_count} parameters</span>
                          <Badge className={getMediumCategoryColor(getMediumCategory(formData.utm_medium))}>
                            {getMediumCategory(formData.utm_medium)}
                          </Badge>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>

              {/* Validation Result */}
              <div className="space-y-6">
                <Card className="bg-zinc-900/50 border-zinc-800">
                  <CardHeader className="pb-4">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <CheckCircle className="w-5 h-5 text-primary" />
                      Validation
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {validationResult ? (
                      <div className="space-y-4">
                        {/* Status */}
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-zinc-400">Status</span>
                          {validationResult.valid ? (
                            <Badge className="bg-emerald-500/20 text-emerald-400">
                              <CheckCircle className="w-3.5 h-3.5 mr-1" />
                              Valid
                            </Badge>
                          ) : (
                            <Badge className="bg-red-500/20 text-red-400">
                              <XCircle className="w-3.5 h-3.5 mr-1" />
                              Invalid
                            </Badge>
                          )}
                        </div>

                        {/* Integrity Score */}
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-zinc-400">Integrity Score</span>
                          <div className={`flex items-center gap-2 px-3 py-1 rounded-full ${getIntegrityScoreBg(validationResult.integrity_score)}`}>
                            <span className={`text-lg font-bold ${getIntegrityScoreColor(validationResult.integrity_score)}`}>
                              {validationResult.integrity_score}
                            </span>
                            <span className="text-xs text-zinc-500">/100</span>
                          </div>
                        </div>

                        {/* Issues */}
                        {validationResult.issues?.length > 0 && (
                          <div className="mt-4 space-y-2">
                            <Label className="text-xs text-zinc-500 uppercase tracking-wider">Issues</Label>
                            {validationResult.issues.map((issue, idx) => (
                              <div
                                key={idx}
                                className="p-3 rounded-md bg-zinc-800/30 border border-zinc-700"
                              >
                                <div className="flex items-start gap-2">
                                  {issue.severity === 'high' ? (
                                    <XCircle className="w-4 h-4 text-red-400 mt-0.5" />
                                  ) : issue.severity === 'medium' ? (
                                    <AlertTriangle className="w-4 h-4 text-amber-400 mt-0.5" />
                                  ) : (
                                    <Info className="w-4 h-4 text-blue-400 mt-0.5" />
                                  )}
                                  <div className="flex-1">
                                    <div className="flex items-center gap-2 mb-1">
                                      <Badge className={`text-[10px] ${
                                        issue.severity === 'high' ? 'bg-red-500/20 text-red-400' :
                                        issue.severity === 'medium' ? 'bg-amber-500/20 text-amber-400' :
                                        'bg-blue-500/20 text-blue-400'
                                      }`}>
                                        {issue.severity}
                                      </Badge>
                                      <span className="text-[10px] text-zinc-500">{issue.param}</span>
                                    </div>
                                    <p className="text-xs text-zinc-300">{issue.message}</p>
                                    {issue.suggestion && (
                                      <p className="text-xs text-emerald-400 mt-1">
                                        Suggestion: {issue.suggestion}
                                      </p>
                                    )}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}

                        {validationResult.issues?.length === 0 && (
                          <div className="p-4 rounded-lg bg-emerald-500/10 border border-emerald-500/20 text-center">
                            <CheckCircle className="w-8 h-8 text-emerald-400 mx-auto mb-2" />
                            <p className="text-sm text-emerald-300">All parameters valid</p>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="text-center py-8 text-zinc-500">
                        <CheckCircle className="w-12 h-12 mx-auto mb-3 opacity-20" />
                        <p className="text-sm">Build a URL to see validation results</p>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Quick Tips */}
                <Card className="bg-zinc-900/50 border-zinc-800">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <Info className="w-4 h-4 text-primary" />
                      Best Practices
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="text-xs space-y-2 text-zinc-500">
                    <p>• Use lowercase values only</p>
                    <p>• Replace spaces with underscores</p>
                    <p>• Use consistent naming across campaigns</p>
                    <p>• Always include source, medium, campaign</p>
                    <p>• Use utm_id for unique tracking</p>
                  </CardContent>
                </Card>
              </div>
            </div>
          </TabsContent>

          {/* Records Tab */}
          <TabsContent value="records" className="mt-6">
            <Card className="bg-zinc-900/50 border-zinc-800">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-lg">UTM Records</CardTitle>
                    <CardDescription>Saved UTM tracking URLs</CardDescription>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={fetchRecords}
                    disabled={recordsLoading}
                  >
                    <RefreshCw className={`w-4 h-4 mr-2 ${recordsLoading ? 'animate-spin' : ''}`} />
                    Refresh
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {records.length > 0 ? (
                  <div className="space-y-3">
                    {records.map((record, idx) => (
                      <div
                        key={record.record_id || idx}
                        className="p-4 rounded-lg bg-zinc-800/30 border border-zinc-700 hover:border-zinc-600 transition-colors"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-2">
                              <Badge variant="outline" className="text-[10px]">
                                {record.utm_source}
                              </Badge>
                              <Badge className={getMediumCategoryColor(getMediumCategory(record.utm_medium))}>
                                {record.utm_medium}
                              </Badge>
                            </div>
                            <code className="text-xs font-mono text-primary break-all block">
                              {record.utm_url}
                            </code>
                            <div className="flex items-center gap-3 mt-2 text-[10px] text-zinc-500">
                              <span>Campaign: {record.utm_campaign}</span>
                              {record.utm_content && <span>Content: {record.utm_content}</span>}
                            </div>
                          </div>
                          <div className="flex items-center gap-2 ml-4">
                            <div className={`px-3 py-1 rounded-full ${getIntegrityScoreBg(record.integrity_score)}`}>
                              <span className={`text-sm font-bold ${getIntegrityScoreColor(record.integrity_score)}`}>
                                {record.integrity_score}
                              </span>
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => copyToClipboard(record.utm_url)}
                            >
                              <Copy className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12 text-zinc-500">
                    <Database className="w-12 h-12 mx-auto mb-3 opacity-20" />
                    <p>No UTM records saved yet</p>
                    <p className="text-xs mt-1">Build and save a URL using the Builder tab</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Parse Tab */}
          <TabsContent value="parse" className="mt-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card className="bg-zinc-900/50 border-zinc-800">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Search className="w-5 h-5 text-primary" />
                    Parse UTM URL
                  </CardTitle>
                  <CardDescription>
                    Extract UTM parameters from an existing URL
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label>URL with UTM Parameters</Label>
                    <Input
                      value={parseInput}
                      onChange={(e) => setParseInput(e.target.value)}
                      placeholder="https://example.com?utm_source=google&utm_medium=cpc..."
                      className="bg-zinc-800/50 border-zinc-700 font-mono text-xs"
                      data-testid="parse-url-input"
                    />
                  </div>
                  <Button
                    onClick={handleParse}
                    disabled={!parseInput.trim()}
                    className="w-full"
                    data-testid="parse-url-btn"
                  >
                    <Search className="w-4 h-4 mr-2" />
                    Parse URL
                  </Button>
                </CardContent>
              </Card>

              <Card className="bg-zinc-900/50 border-zinc-800">
                <CardHeader>
                  <CardTitle className="text-lg">Parse Result</CardTitle>
                </CardHeader>
                <CardContent>
                  {parseResult ? (
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <Label className="text-xs text-zinc-500">Base URL</Label>
                        <code className="text-sm text-zinc-300 block break-all">
                          {parseResult.base_url}
                        </code>
                      </div>

                      <div className="space-y-2">
                        <Label className="text-xs text-zinc-500">UTM Parameters</Label>
                        {Object.entries(parseResult.utm_params || {}).map(([key, value]) => (
                          <div key={key} className="flex items-center justify-between py-2 border-b border-zinc-800 last:border-0">
                            <span className="text-sm text-zinc-400">{key}</span>
                            <span className="text-sm font-mono text-primary">{value}</span>
                          </div>
                        ))}
                      </div>

                      {Object.keys(parseResult.other_params || {}).length > 0 && (
                        <div className="space-y-2">
                          <Label className="text-xs text-zinc-500">Other Parameters</Label>
                          {Object.entries(parseResult.other_params).map(([key, value]) => (
                            <div key={key} className="flex items-center justify-between py-2 border-b border-zinc-800 last:border-0">
                              <span className="text-sm text-zinc-400">{key}</span>
                              <span className="text-sm text-zinc-300">{value}</span>
                            </div>
                          ))}
                        </div>
                      )}

                      <div className="flex gap-2 pt-2">
                        <Badge className={parseResult.has_utm ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'}>
                          {parseResult.has_utm ? 'Has UTM' : 'No UTM'}
                        </Badge>
                        <Badge className={parseResult.utm_complete ? 'bg-emerald-500/20 text-emerald-400' : 'bg-amber-500/20 text-amber-400'}>
                          {parseResult.utm_complete ? 'Complete' : 'Incomplete'}
                        </Badge>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-8 text-zinc-500">
                      <Search className="w-12 h-12 mx-auto mb-3 opacity-20" />
                      <p className="text-sm">Enter a URL to parse</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Reference Tab */}
          <TabsContent value="reference" className="mt-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card className="bg-zinc-900/50 border-zinc-800">
                <CardHeader>
                  <CardTitle className="text-lg">Standard UTM Parameters</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {referenceData?.standard_params && Object.entries(referenceData.standard_params).map(([param, info]) => (
                    <div key={param} className="p-4 rounded-lg bg-zinc-800/30 border border-zinc-700">
                      <div className="flex items-center justify-between mb-2">
                        <code className="text-sm font-mono text-primary">{param}</code>
                        <Badge className={info.required ? 'bg-red-500/20 text-red-400' : 'bg-zinc-500/20 text-zinc-400'}>
                          {info.required ? 'Required' : 'Optional'}
                        </Badge>
                      </div>
                      <p className="text-xs text-zinc-400 mb-2">{info.description}</p>
                      <div className="flex flex-wrap gap-1">
                        {info.examples?.map((ex, idx) => (
                          <Badge key={idx} variant="outline" className="text-[10px]">
                            {ex}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>

              <Card className="bg-zinc-900/50 border-zinc-800">
                <CardHeader>
                  <CardTitle className="text-lg">Medium Categories</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {referenceData?.medium_categories && Object.entries(referenceData.medium_categories).map(([category, mediums]) => (
                    <div key={category} className="p-4 rounded-lg bg-zinc-800/30 border border-zinc-700">
                      <div className="flex items-center gap-2 mb-3">
                        <Badge className={getMediumCategoryColor(category)}>
                          {category}
                        </Badge>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {mediums.map((medium, idx) => (
                          <code key={idx} className="text-xs px-2 py-1 rounded bg-zinc-800 text-zinc-300">
                            {medium}
                          </code>
                        ))}
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
}
